import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Orcamento, Regime } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves, useFamilias, useMunicipios, useOrcamentos } from "../api/consultas";
import { podeEditar, useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { MunicipioBusca } from "../components/MunicipioBusca";
import { BadgeRegime, Carregando, ErroCarregamento, formatarData, semAutocompletar, Tabela } from "../components/ui";

function podeGerenciar(o: Orcamento, usuario: ReturnType<typeof useAuth>["usuario"]) {
  if (!usuario || !podeEditar(usuario)) return false;
  return usuario.papel === "admin" || o.criado_por === usuario.id;
}

export function OrcamentosLista() {
  const { data, isLoading, error } = useOrcamentos();
  const { usuario } = useAuth();
  const { avisar, avisarErro } = useAvisos();
  const queryClient = useQueryClient();
  const isAdmin = usuario?.papel === "admin";

  const aposRemover = (r: { mensagem: string }) => {
    avisar(r.mensagem);
    queryClient.invalidateQueries({ queryKey: chaves.orcamentos });
    queryClient.invalidateQueries({ queryKey: chaves.painel });
  };
  const limparTodos = useMutation({
    mutationFn: () => api<{ mensagem: string }>("/api/orcamentos/limpar", { method: "POST", body: { escopo: "todos" } }),
    onSuccess: aposRemover,
    onError: avisarErro,
  });
  const remover = useMutation({
    mutationFn: (id: number) => api<{ mensagem: string }>(`/api/orcamentos/${id}`, { method: "DELETE" }),
    onSuccess: aposRemover,
    onError: avisarErro,
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;

  return (
    <>
      <h1>Orçamentos</h1>
      <div className="form-row">
        <Link className="btn btn-orange" to="/orcamentos/novo">
          + Novo orçamento
        </Link>
        {data.length > 0 && podeEditar(usuario) && (
          <button
            type="button"
            className="btn-danger"
            disabled={limparTodos.isPending}
            onClick={() => {
              const msg = isAdmin
                ? "Remover TODOS os orçamentos de TODOS os usuários? Essa ação não pode ser desfeita."
                : "Remover todos os SEUS orçamentos? Essa ação não pode ser desfeita.";
              if (confirm(msg)) limparTodos.mutate();
            }}
          >
            {isAdmin ? "Limpar todos os orçamentos" : "Limpar meus orçamentos"}
          </button>
        )}
      </div>
      <div className="card" style={{ marginTop: 16 }}>
        {data.length ? (
          <Tabela>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Município</th>
                <th>Regime</th>
                <th>Criado em</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((o) => (
                <tr key={o.id}>
                  <td>{o.nome}</td>
                  <td>{o.municipio_nome || "—"}</td>
                  <td>
                    <BadgeRegime regime={o.regime} />
                  </td>
                  <td>{formatarData(o.criado_em)}</td>
                  <td>
                    <Link className="btn btn-ghost" to={`/orcamentos/${o.id}`}>
                      Abrir
                    </Link>
                  </td>
                  <td>
                    {podeGerenciar(o, usuario) && (
                      <button
                        type="button"
                        className="btn-danger"
                        disabled={remover.isPending}
                        onClick={() => {
                          if (confirm(`Excluir o orçamento '${o.nome}'? Essa ação não pode ser desfeita.`)) remover.mutate(o.id);
                        }}
                      >
                        excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        ) : (
          <p className="note">Nenhum orçamento criado ainda.</p>
        )}
      </div>
    </>
  );
}

export function OrcamentoNovo() {
  const municipios = useMunicipios();
  const familias = useFamilias();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { avisarErro } = useAvisos();
  const [nome, setNome] = useState("");
  const [municipioId, setMunicipioId] = useState<number | null>(null);
  const [familiaId, setFamiliaId] = useState("");
  const [regime, setRegime] = useState<Regime>("desonerada");

  const criar = useMutation({
    mutationFn: () =>
      api<{ id: number }>("/api/orcamentos", {
        method: "POST",
        body: { nome, municipio_id: municipioId, familia_id: familiaId, regime },
      }),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: chaves.orcamentos });
      queryClient.invalidateQueries({ queryKey: chaves.painel });
      navigate(`/orcamentos/${id}`);
    },
    onError: avisarErro,
  });

  if (municipios.isLoading || familias.isLoading) return <Carregando />;

  return (
    <>
      <h1>Novo orçamento</h1>
      <div className="card" style={{ maxWidth: 520 }}>
        <form
          onSubmit={(ev: FormEvent) => {
            ev.preventDefault();
            criar.mutate();
          }}
        >
          <div className="form-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
            <label>
              Nome do orçamento
              <input type="text" required placeholder="ex: SES Bairro X - Rede coletora" value={nome} onChange={(e) => setNome(e.target.value)} {...semAutocompletar} />
            </label>
            <MunicipioBusca
              rotulo="Município (define sindicato/encargos e ISS p/ BDI)"
              municipios={municipios.data ?? []}
              valor={municipioId}
              onChange={setMunicipioId}
            />
            <label>
              Família de serviço (qual tabela de preços usar)
              <select required value={familiaId} onChange={(e) => setFamiliaId(e.target.value)}>
                <option value="">— selecione —</option>
                {(familias.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Regime
              <select value={regime} onChange={(e) => setRegime(e.target.value as Regime)}>
                <option value="desonerada">Desonerada (CPRB)</option>
                <option value="tradicional">Tradicional</option>
              </select>
            </label>
          </div>
          <button type="submit" className="btn-orange" disabled={criar.isPending}>
            Criar orçamento
          </button>
        </form>
        <p className="note" style={{ marginTop: 10 }}>
          Não encontrou o município? Cadastre em <Link to="/municipios">Municípios / Sindicatos</Link>.
        </p>
      </div>
    </>
  );
}
